import React from 'react';
import posterPlaceholder from '../utils/posterPlaceholder';
import { SPECIES } from '../theme/species';
import { realPlace } from '../utils/placeLabel';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * SquareHeader — full-bleed city banner behind the town name + live count.
 *
 * The banner is a Mapbox Static satellite image generated from the city's coords:
 * high-res, the SAME format for every city, and always the correct place (no
 * wrong-city photos, no per-city quality lottery). Falls back to a deterministic
 * gradient if the coords/token are missing.
 */
function mapboxBanner(lat, lng) {
  if (!MAPBOX_TOKEN || lat == null || lng == null) return null;
  // satellite-streets @2x, fixed zoom/size → uniform across the app.
  // Mapbox Static caps each dimension at 1280px; @2x renders it at 2x (2560×760).
  return `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/${lng},${lat},11.5,0/1280x380@2x?access_token=${MAPBOX_TOKEN}&logo=false&attribution=false`;
}

export default function SquareHeader({ square, onChangeTown, isAdmin = false, onRegenerate, regenerating = false }) {
  const gradient = posterPlaceholder(square?.slug || 'square');
  // A finished OpenAI banner wins; otherwise the instant Mapbox satellite.
  const aiBanner = square?.bannerSource === 'openai' ? square?.bannerImageUrl : null;
  const mapbox = mapboxBanner(square?.centerLat, square?.centerLng);
  const banner = aiBanner || mapbox;
  const cyan = SPECIES.signal; // #03FFFF

  return (
    <div className="border-b-2 border-black">
      <div className="relative h-44 w-full overflow-hidden sm:h-60" style={{ background: banner ? '#000' : gradient }}>
        {banner && (
          <img src={banner} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover"  referrerPolicy="no-referrer"/>
        )}
        {/* legibility scrim */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.82), rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.3))' }} />
        {/* cyan Town-Square strip */}
        <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: cyan }} />

        {/* Admin: re-roll a fresh OpenAI banner */}
        {isAdmin && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating}
            title="Generate a new banner image"
            className="absolute right-3 top-4 flex items-center gap-1 border border-white/50 bg-black/50 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur-sm hover:bg-black/80 disabled:opacity-50"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={regenerating ? 'animate-spin' : ''}>
              <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {regenerating ? 'Generating…' : 'New image'}
          </button>
        )}

        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6">
          <div className="mx-auto max-w-3xl">
            <div className="font-mono text-[10px] font-bold uppercase tracking-widest" style={{ color: cyan }}>
              The City Portal · Loud on purpose
            </div>
            <button type="button" onClick={onChangeTown} className="mt-1 flex items-center gap-2 text-left hover:opacity-90">
              <h1 className="font-mono text-3xl font-bold uppercase tracking-wider text-white sm:text-4xl">
                {realPlace(square?.label) || '…'}
              </h1>
              <span className="text-lg text-white/90">▾</span>
            </button>
            <div className="mt-1 font-mono text-xs text-white/85">
              {square?.liveCount > 0
                ? `${square.liveCount} live in the city portal`
                : 'Quiet right now — send the first Portal'}
            </div>
          </div>
        </div>
      </div>

      {banner && !aiBanner && (
        <div className="bg-black px-3 py-1 text-right">
          <a
            href="https://www.mapbox.com/about/maps/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[9px] text-white/40 hover:text-white/70"
          >
            © Mapbox © Maxar
          </a>
        </div>
      )}
    </div>
  );
}
